/**
 * Tiny IndexedDB wrapper for the field app's offline store (no library).
 *
 * Database "assetos-field":
 *   ops   - the offline action queue shared by every field screen (keyPath clientId, index by seq)
 *   cache - downloaded data for offline use (keyPath key): { key, value, savedAt, bytes }
 *   meta  - small settings / timestamps (keyPath key)
 *
 * When IndexedDB is unavailable (old browser, private mode quirks) an in-memory store is used so the app still works
 * for the session; nothing then survives a reload.
 */
const DB_NAME = 'assetos-field';
const DB_VERSION = 1;
export const STORES = ['ops', 'cache', 'meta'];

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      return resolve(null);
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('ops')) db.createObjectStore('ops', { keyPath: 'clientId' }).createIndex('seq', 'seq');
      if (!db.objectStoreNames.contains('cache')) db.createObjectStore('cache', { keyPath: 'key' });
      if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta', { keyPath: 'key' });
    };
    req.onsuccess = () => {
      const db = req.result;
      // Another tab upgrading the schema: let it, and reopen next time.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  return dbPromise;
}

const memory = { ops: new Map(), cache: new Map(), meta: new Map() };
const keyOf = (store, row) => (store === 'ops' ? row.clientId : row.key);

const wrap = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

async function run(store, mode, fn) {
  const db = await openDb();
  if (!db) return fn(null);
  const tx = db.transaction(store, mode);
  const result = await fn(tx.objectStore(store));
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
  });
  return result;
}

export function idbGet(store, key) {
  return run(store, 'readonly', (os) => (os ? wrap(os.get(key)) : memory[store].get(key)));
}

export function idbAll(store) {
  return run(store, 'readonly', (os) => (os ? wrap(os.getAll()) : [...memory[store].values()]));
}

export function idbPut(store, row) {
  return run(store, 'readwrite', (os) => {
    if (os) return wrap(os.put(row));
    memory[store].set(keyOf(store, row), row);
    return undefined;
  });
}

export function idbPutMany(store, rows) {
  return run(store, 'readwrite', (os) => {
    rows.forEach((row) => (os ? os.put(row) : memory[store].set(keyOf(store, row), row)));
    return rows.length;
  });
}

export function idbDelete(store, key) {
  return run(store, 'readwrite', (os) => (os ? wrap(os.delete(key)) : memory[store].delete(key)));
}

export function idbClear(store) {
  return run(store, 'readwrite', (os) => (os ? wrap(os.clear()) : memory[store].clear()));
}

/** True when data is kept in IndexedDB (survives reloads), false when only in memory. */
export async function isPersistent() {
  return Boolean(await openDb());
}
